// Mobile Chrome cache debugging and invalidation utilities

export interface MobileCacheInfo {
  userAgent: string
  isChrome: boolean
  isMobile: boolean
  cacheSize?: number
  storageQuota?: number
}

export const getMobileCacheInfo = (): MobileCacheInfo => {
  const userAgent = navigator.userAgent
  const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor)
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)
  
  return {
    userAgent,
    isChrome,
    isMobile,
  }
}

export const clearMobileChromeCache = async (): Promise<void> => {
  const cacheInfo = getMobileCacheInfo()
  
  if (!cacheInfo.isChrome || !cacheInfo.isMobile) {
    console.log('[MobileCache] Not mobile Chrome, skipping cache clearing')
    return
  }

  console.log('[MobileCache] Clearing mobile Chrome cache...')
  
  try {
    // Clear service worker caches if present
    if ('caches' in window) {
      const cacheNames = await caches.keys()
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      )
      console.log('[MobileCache] Service worker caches cleared')
    }

    // Clear application cache if available
    if ('applicationCache' in window) {
      const appCache = (window as any).applicationCache
      if (appCache && appCache.status !== appCache.UNCACHED) {
        try {
          appCache.update()
          console.log('[MobileCache] Application cache update triggered')
        } catch (error) {
          console.warn('[MobileCache] Failed to update application cache:', error)
        }
      }
    }

    // Force reload to clear in-memory cache
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' })
    }

    // Clear any potential error storage
    const errorKeys = ['sms_global_error', 'sms_error_timestamp', 'last_error_state']
    errorKeys.forEach(key => {
      try {
        localStorage.removeItem(key)
        sessionStorage.removeItem(key)
      } catch (error) {
        console.warn(`[MobileCache] Failed to remove ${key}:`, error)
      }
    })

    console.log('[MobileCache] Cache clearing completed')
  } catch (error) {
    console.error('[MobileCache] Error during cache clearing:', error)
  }
}

export const addCacheBustingToUrl = (url: string): string => {
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}_t=${Date.now()}&_cb=${Math.random().toString(36).substring(2)}`
}

export const forceRefreshForMobileChrome = (): void => {
  const cacheInfo = getMobileCacheInfo()
  
  if (cacheInfo.isChrome && cacheInfo.isMobile) {
    console.log('[MobileCache] Detected mobile Chrome, forcing refresh...')
    
    // Add cache-busting parameter to current URL
    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.set('_force_refresh', Date.now().toString())
    
    // Use replace to avoid creating history entries
    window.location.replace(currentUrl.toString())
  }
}

export const setupMobileChromeCacheMonitoring = (): void => {
  const cacheInfo = getMobileCacheInfo()
  
  if (!cacheInfo.isChrome || !cacheInfo.isMobile) {
    return
  }

  console.log('[MobileCache] Setting up cache monitoring for mobile Chrome')
  
  // Monitor for navigation events that might indicate caching issues
  let lastNavigationTime = Date.now()
  
  const checkForStaleContent = () => {
    const currentTime = Date.now()
    const timeSinceLastNav = currentTime - lastNavigationTime
    
    // If navigation was too fast, it might be from cache
    if (timeSinceLastNav < 100) {
      console.warn('[MobileCache] Fast navigation detected, possible cache issue')
      
      // Clear any stale error states
      const errorKeys = ['sms_global_error', 'sms_error_timestamp']
      errorKeys.forEach(key => {
        try {
          localStorage.removeItem(key)
          sessionStorage.removeItem(key)
        } catch (error) {
          // Ignore errors during cleanup
        }
      })
    }
    
    lastNavigationTime = currentTime
  }

  // Listen for navigation events
  window.addEventListener('popstate', checkForStaleContent)
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      console.warn('[MobileCache] Page loaded from cache (bfcache)')
      checkForStaleContent()
    }
  })

  // Add visibility change listener for when app comes to foreground
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      console.log('[MobileCache] App became visible, checking for stale content')
      checkForStaleContent()
    }
  })
}

// Export a function to be called on app initialization
export const initializeMobileChromeSupport = (): void => {
  setupMobileChromeCacheMonitoring()
  
  // Log mobile Chrome detection
  const cacheInfo = getMobileCacheInfo()
  if (cacheInfo.isChrome && cacheInfo.isMobile) {
    console.log('[MobileCache] Mobile Chrome detected - cache management enabled')
    
    // Add a global function for manual cache clearing (debugging)
    ;(window as any).clearMobileCache = clearMobileChromeCache
    ;(window as any).forceRefresh = forceRefreshForMobileChrome
  }
}
